import React, { useEffect, useState, useCallback } from "react";
import {
  getCheck,
  manuallyApproveCheck,
  manuallyRejectCheck,
  deliverCheckReminder,
  createCheck,
} from "services/wpApi";
import ImagePreviewLite, { buildPhotos } from "./ImagePreviewLite";
import { downloadImage } from "utils/downloadImage";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./orderWidget.css";

// Maps a check's step + verification result to a human label and a tone used
// for the status pill. Mirrors src/components/checks/StatusBadge.js and the
// canonical statuses in includes/Models/IDCheck.php.
function getStatus(check) {
  const step = check.step;
  const success = check?.job?.result?.success;
  // manuallyApprove/manuallyReject on the core side writes step="completed"
  // + step_manually_overridden=true rather than a distinct step value, so the
  // override flag is the only thing distinguishing a manual decision from an
  // auto-verified / auto-failed terminal state.
  const overridden = check?.step_manually_overridden;

  if (step === "completed") {
    if (overridden) {
      return success
        ? { label: "Manually approved", tone: "success" }
        : { label: "Manually rejected", tone: "critical" };
    }
    return success
      ? { label: "Verified", tone: "success" }
      : { label: "Failed verification", tone: "critical" };
  }

  switch (step) {
    // Legacy: kept as a safety net for any older checks in MongoDB that have
    // these step values; new checks coming through manuallyApprove/Reject
    // hit the "completed" + override branch above.
    case "manually_approved":
      return { label: "Manually approved", tone: "success" };
    case "manually_rejected":
      return { label: "Manually rejected", tone: "critical" };
    case "failed":
      return { label: "Failed verification", tone: "critical" };
    case "in_review":
      return { label: "In review", tone: "warning" };
    case "delivered":
      return { label: "Delivered", tone: "info" };
    case "opened":
      return { label: "Link opened", tone: "info" };
    case "id":
      return { label: "1 of 2 photos submitted", tone: "info" };
    case "face_match":
      return { label: "Face match in progress", tone: "info" };
    default:
      return { label: "In progress", tone: "info" };
  }
}

const IN_PROGRESS_STEPS = ["delivered", "opened", "id", "face_match"];

function isApproved(check) {
  return (
    check.step === "manually_approved" ||
    (check.step === "completed" && check?.job?.result?.success === true)
  );
}

function isRejected(check) {
  return (
    check.step === "manually_rejected" ||
    check.step === "failed" ||
    (check.step === "completed" && check?.job?.result?.success === false)
  );
}

function deliveryMethodsFor(check) {
  const methods = [];
  if (check.email) methods.push("email");
  if (check.phone) methods.push("phone");
  return methods;
}

// Steps where the customer has finished (or is past) submitting documents, so
// the absence of photos is meaningful rather than just "not yet uploaded".
const CONCLUDED_STEPS = [
  "completed",
  "in_review",
  "manually_approved",
  "manually_rejected",
  "failed",
];

function hasAnyPhoto(check) {
  return Boolean(
    check.idPhoto ||
      check.backIdPhoto ||
      check.userPhoto ||
      check.proofOfAddress ||
      check.signature
  );
}

// Distinguishes "customer never uploaded anything" from an age-plan shop where
// photos are intentionally stripped server-side but extracted data (name, DOB)
// is still returned.
function hasExtractedData(check) {
  const result = check?.job?.result || {};
  return Boolean(
    result.firstName || result.lastName || result.birthDate || result.idAddress
  );
}

function CheckLinkRow({ check }) {
  const [copied, setCopied] = useState(false);
  if (!check?.id) return null;
  // Order widget runs in the lean bundle without access to shop settings, so
  // it falls back to the default flow URL. Merchants on customFlowUrl will
  // still see the customer-facing default link here.
  const url = `https://verify.getverdict.com/${check.id}`;

  function copy() {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  }

  return (
    <div className="real-id-order-widget__check-link">
      <span className="real-id-order-widget__check-link-label">
        ID check link
      </span>
      <div className="real-id-order-widget__check-link-row">
        <input
          type="text"
          readOnly
          value={url}
          className="real-id-order-widget__check-link-input"
          onFocus={(e) => e.target.select()}
        />
        <button type="button" className="button" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function PhotoCarousel({ photos, sandboxMode, onRequestDownload }) {
  const [idx, setIdx] = useState(0);
  if (!photos.length) return null;

  // Single photo — no carousel chrome, just the preview.
  if (photos.length === 1) {
    return (
      <div className="real-id-order-widget__photos">
        <ImagePreviewLite
          photo={photos[0]}
          sandboxMode={sandboxMode}
          onRequestDownload={onRequestDownload}
        />
      </div>
    );
  }

  const safeIdx = Math.min(idx, photos.length - 1);
  const photo = photos[safeIdx];

  return (
    <div className="real-id-order-widget__photos">
      <ImagePreviewLite
        key={safeIdx}
        photo={photo}
        sandboxMode={sandboxMode}
        onRequestDownload={onRequestDownload}
      />
      <div className="real-id-order-widget__carousel-controls">
        <button
          type="button"
          aria-label="Previous photo"
          className="real-id-order-widget__rotate-btn"
          onClick={() =>
            setIdx((safeIdx - 1 + photos.length) % photos.length)
          }
        >
          <FaChevronLeft />
        </button>
        <div className="real-id-order-widget__carousel-indicator">
          {safeIdx + 1} of {photos.length}
        </div>
        <button
          type="button"
          aria-label="Next photo"
          className="real-id-order-widget__rotate-btn"
          onClick={() => setIdx((safeIdx + 1) % photos.length)}
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

function LiabilityDialog({ onAccept, onCancel }) {
  const [accepted, setAccepted] = useState(false);

  return (
    <div
      className="real-id-order-widget__liability-dialog-backdrop"
      onClick={onCancel}
      role="dialog"
      aria-label="Acknowledge liability"
    >
      <div
        className="real-id-order-widget__liability-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>Acknowledge Liability</h3>
        <p>
          By downloading customer photos, you acknowledge full responsibility
          for securing this data.
        </p>
        <p>
          You also agree to delete the image as required under privacy laws like
          GDPR, CCPA, or other relevant regulations.
        </p>
        <label>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          I acknowledge and accept liability for downloading customer submitted
          images
        </label>
        <div className="real-id-order-widget__actions">
          <button
            type="button"
            className="button button-primary"
            disabled={!accepted}
            onClick={onAccept}
          >
            Accept
          </button>
          <button type="button" className="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="real-id-order-widget__field">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export default function OrderCheckWidget({ checkId, detailsUrl }) {
  const [loading, setLoading] = useState(true);
  const [check, setCheck] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirming, setConfirming] = useState(null); // "approve" | "reject" | "resend" | null
  const [cancelAndRefund, setCancelAndRefund] = useState(false);
  const [restockInventory, setRestockInventory] = useState(false);
  // Session-local liability acknowledgement — there is no per-WP-user record
  // on the core side to persist this against, so it resets with the widget.
  const [downloadAcknowledged, setDownloadAcknowledged] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  function requestDownload(photo) {
    if (downloadAcknowledged) {
      downloadImage(photo.src);
    } else {
      setPendingDownload(photo);
    }
  }

  function acknowledgeAndDownload() {
    setDownloadAcknowledged(true);
    if (pendingDownload) {
      downloadImage(pendingDownload.src);
      setPendingDownload(null);
    }
  }

  const loadCheck = useCallback(() => {
    setLoading(true);
    return getCheck(checkId)
      .then((res) => {
        setCheck(res?.data?.check || null);
        setError(null);
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          setError("You do not have permission to view ID verification checks.");
        } else {
          setError("Unable to load the ID verification details. Please refresh the page.");
        }
      })
      .finally(() => setLoading(false));
  }, [checkId]);

  useEffect(() => {
    loadCheck();
  }, [loadCheck]);

  function run(promise, successMessage) {
    setBusy(true);
    setMessage(null);
    return promise
      .then(() => {
        setConfirming(null);
        setMessage({ tone: "success", text: successMessage });
        return loadCheck();
      })
      .catch((err) => {
        if (err?.isNonceError) {
          setMessage({
            tone: "error",
            text:
              "Your WordPress session has expired. Reload this order page and try again.",
          });
          return;
        }
        setMessage({
          tone: "error",
          text:
            err?.response?.data?.message ||
            "Something went wrong. Please try again.",
        });
      })
      .finally(() => setBusy(false));
  }

  function handleApprove() {
    run(manuallyApproveCheck(checkId), "ID check approved.");
  }

  function handleReject() {
    run(
      manuallyRejectCheck(checkId, {
        wc_order_id: check.wcOrderId,
        cancel_and_refund: cancelAndRefund,
        restock_inventory: restockInventory,
      }),
      "ID check rejected."
    );
  }

  function handleReminder() {
    run(
      deliverCheckReminder(checkId, {
        deliveryMethods: deliveryMethodsFor(check),
        phone: check.phone,
        email: check.email,
        firstName: check.firstName,
        lastName: check.lastName,
      }),
      "Reminder sent to the customer."
    );
  }

  function handleResend() {
    run(
      createCheck({
        deliveryMethods: deliveryMethodsFor(check),
        phone: check.phone,
        email: check.email,
        firstName: check.firstName,
        lastName: check.lastName,
        orderId: check.orderId,
        wcOrderId: check.wcOrderId,
        wcCustomerId: check.wcCustomerId,
        triggeredRules: check.triggeredRules,
        source: "resendIdCheck",
      }),
      "A new ID check was sent to the customer."
    );
  }

  if (loading && !check) {
    return (
      <div className="real-id-order-widget real-id-order-widget--loading">
        Loading ID verification details…
      </div>
    );
  }

  if (error) {
    return (
      <div className="real-id-order-widget">
        <div className="real-id-order-widget__notice real-id-order-widget__notice--error">
          {error}
        </div>
      </div>
    );
  }

  if (!check) {
    return null;
  }

  const status = getStatus(check);
  const result = check?.job?.result || {};
  const address = result.idAddress;
  const applicant =
    [result.firstName, result.lastName].filter(Boolean).join(" ") ||
    [check.firstName, check.lastName].filter(Boolean).join(" ") ||
    "Unnamed customer";

  const inProgress =
    IN_PROGRESS_STEPS.includes(check.step) && check.step !== "completed";

  const photosPresent = hasAnyPhoto(check);
  const extractedDataPresent = hasExtractedData(check);
  const concluded = CONCLUDED_STEPS.includes(check.step);
  const noPhotosUploaded = !photosPresent && concluded && !extractedDataPresent;
  // Concluded checks (manually overridden, auto-failed, in review) where the
  // OCR/extraction step never produced usable fields. Without an explicit
  // empty state the left column just renders blank and a staff member can't
  // tell whether data is missing vs still loading.
  const noIdDetailsRead = !extractedDataPresent && concluded;

  return (
    <div className="real-id-order-widget">
      <div className="real-id-order-widget__header">
        <span
          className={`real-id-order-widget__status real-id-order-widget__status--${status.tone}`}
        >
          {status.label}
        </span>
        <span className="real-id-order-widget__applicant">{applicant}</span>
      </div>

      {check.isUnderageCustomer && (
        <div className="real-id-order-widget__notice real-id-order-widget__notice--error">
          This customer passed ID verification but did not meet your store's
          minimum age requirement.
        </div>
      )}

      <div className="real-id-order-widget__main">
        {(extractedDataPresent || noIdDetailsRead) && (
          <div className="real-id-order-widget__main-col">
            <span className="real-id-order-widget__check-link-label">
              ID details
            </span>
            {extractedDataPresent ? (
              <dl className="real-id-order-widget__fields">
                <Field
                  label="Name on ID"
                  value={
                    result.firstName
                      ? [result.firstName, result.lastName]
                          .filter(Boolean)
                          .join(" ")
                      : undefined
                  }
                />
                {address && (
                  <Field
                    label="Address on ID"
                    value={[
                      [address.streetNumber, address.street]
                        .filter(Boolean)
                        .join(" "),
                      address.unit,
                      [address.city, address.state, address.postalCode]
                        .filter(Boolean)
                        .join(", "),
                      address.country,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                )}
                {!address && Array.isArray(result.unverifiedIdAddress) && (
                  <Field
                    label="Address on ID"
                    value={result.unverifiedIdAddress.join(", ")}
                  />
                )}
                <Field label="Birth date" value={result.birthDate} />
                <Field label="Expiration date" value={result.expireDate} />
              </dl>
            ) : (
              <div className="real-id-order-widget__notice real-id-order-widget__notice--info">
                No ID details were read for this check.
              </div>
            )}
          </div>
        )}

        {(photosPresent || noPhotosUploaded) && (
          <div className="real-id-order-widget__main-col">
            <span className="real-id-order-widget__check-link-label">
              Photos
            </span>
            {photosPresent ? (
              <PhotoCarousel
                photos={buildPhotos(check)}
                sandboxMode={check.sandboxMode}
                onRequestDownload={requestDownload}
              />
            ) : (
              <div className="real-id-order-widget__notice real-id-order-widget__notice--info">
                This customer didn't upload any ID photos.
              </div>
            )}
          </div>
        )}
      </div>

      <CheckLinkRow check={check} />

      {message && (
        <div
          className={`real-id-order-widget__notice real-id-order-widget__notice--${
            message.tone === "success" ? "success" : "error"
          }`}
        >
          {message.text}
        </div>
      )}

      {confirming === "reject" && (
        <div className="real-id-order-widget__confirm">
          <p>Reject this ID check? The check will be marked as failed.</p>
          {check.wcOrderId && (
            <label>
              <input
                type="checkbox"
                checked={cancelAndRefund}
                onChange={(e) => setCancelAndRefund(e.target.checked)}
              />
              Cancel and refund the order
            </label>
          )}
          {check.wcOrderId && cancelAndRefund && (
            <label>
              <input
                type="checkbox"
                checked={restockInventory}
                onChange={(e) => setRestockInventory(e.target.checked)}
              />
              Restock inventory
            </label>
          )}
          <div className="real-id-order-widget__actions">
            <button
              type="button"
              className="button button-primary"
              disabled={busy}
              onClick={handleReject}
            >
              Confirm rejection
            </button>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => setConfirming(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirming === "approve" && (
        <div className="real-id-order-widget__confirm">
          <p>Approve this ID check? The check will be marked as verified.</p>
          <div className="real-id-order-widget__actions">
            <button
              type="button"
              className="button button-primary"
              disabled={busy}
              onClick={handleApprove}
            >
              Confirm approval
            </button>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => setConfirming(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirming === "resend" && (
        <div className="real-id-order-widget__confirm">
          <p>
            Send a new ID check
            {check.email || check.phone ? (
              <>
                {" to "}
                <strong>{check.email || check.phone}</strong>
              </>
            ) : null}
            ? This creates a fresh check and re-delivers the link to the
            customer.
          </p>
          <div className="real-id-order-widget__actions">
            <button
              type="button"
              className="button button-primary"
              disabled={busy}
              onClick={handleResend}
            >
              Send new ID check
            </button>
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => setConfirming(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!confirming && (
        <div className="real-id-order-widget__actions">
          {!isApproved(check) && (
            <button
              type="button"
              className="button button-primary"
              disabled={busy}
              onClick={() => setConfirming("approve")}
            >
              Approve
            </button>
          )}
          {!isRejected(check) && (
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => setConfirming("reject")}
            >
              Reject
            </button>
          )}
          {inProgress && (
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={handleReminder}
            >
              Send reminder
            </button>
          )}
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={() => setConfirming("resend")}
          >
            Resend
          </button>
        </div>
      )}

      {detailsUrl && (
        <div className="real-id-order-widget__footer">
          <a
            className="real-id-order-widget__details-link"
            href={detailsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View full check details
          </a>
        </div>
      )}

      {pendingDownload && (
        <LiabilityDialog
          onAccept={acknowledgeAndDownload}
          onCancel={() => setPendingDownload(null)}
        />
      )}
    </div>
  );
}
